import { useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useAgentStream, useSendMessage } from '@/hooks/agents/useAgentStream';
import { toast } from 'sonner';
import { useAgentStreamSocket } from '@/hooks/agents/useAgentStreamSocket';
import { useAgentQueueSocket } from '@/hooks/agents/useAgentQueueSocket';
import {
  deriveTurnActivity,
  useAgentActivityFromStream,
} from '@/hooks/agents/useAgentActivityFromStream';
import { useInterruptAgent, useStopAgent, useCompleteAgent } from '@/hooks/agents/useAgents';
import { useMessageQueue } from '@/hooks/agents/useMessageQueue';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';
import { randomClientId } from '@reflex/ui/lib/random-id';
import { useAgentTypeRegistry } from '@/lib/agent-type-registry';
import {
  AgentStreamDataContext,
  type AgentStreamData,
  type PendingUserMessage,
} from './AgentStreamDataContext';
import {
  activityWithPendingSend,
  describeCommandFailure,
  getInitialPromptPending,
  nextStatusOnTimeout,
  reconcilePending,
  stripAttachmentBlobs,
} from './pending-messages';
import { usePendingEchoTimeout } from './usePendingEchoTimeout';
import { useCommandOutcomeWatch, type CommandOutcome } from './useCommandOutcomeWatch';
import { api } from '@/lib/api';
import type { AgentCommandPendingResponse } from '@reflex/shared';
import type { Agent, Attachment } from '@/lib/types';
import { isWorkingActivity } from '@/lib/status-colors';
import { isCodexSlashCommand } from '@reflex/plugin-chat/web/components/sdk-chat/codex-handler';
import { parseModelCommand } from '@reflex/shared';
import { modelSwitchErrorMessage } from '@/components/agent/agent-detail/model-switch-copy';

/**
 * Cap on the number of pending entries kept in `localStorage` per agent.
 * Guards against pathological accumulation if a client is offline for a
 * very long time. Trimming is FIFO (oldest first).
 */
const MAX_PENDING_MESSAGES = 20;

/**
 * If a `'sending'` pending entry has not been echoed back by the live
 * stream within this many milliseconds, surface it to the user. The
 * exact outcome depends on whether the HTTP send was acknowledged:
 *
 * - HTTP acked (2xx received) → `unconfirmed` — the message reached
 *   the server and Axon publish succeeded, but we never saw the echo.
 *   Probably delivered; soft warning, no toast.
 * - HTTP not yet acked (still in flight or never returned) → `failed`
 *   — we have no evidence the message was delivered. Hard error.
 *
 * Sized for the slow-but-healthy path: a message to a sleeping agent
 * has to wake the devbox and rebuild the agent's connection before the
 * prompt can be dispatched and echoed, which routinely takes 15–25s.
 * Hard failures do not wait this long — a 202 send is watched through
 * its command id ({@link useCommandOutcomeWatch}) and a refused delivery
 * flips the bubble to `failed` as soon as the server records it.
 *
 * `createdAt` is persisted, so this timeout also applies after a
 * refresh: a stuck entry surfaces immediately on rehydrate.
 */
const SEND_ECHO_TIMEOUT_MS = 60_000;

/**
 * The 202 body of a send whose outcome outlasted the server's bounded
 * wait, as opposed to the 201 acknowledging event. The generated client
 * unwraps both to their bodies, so the shape is what tells them apart.
 */
function isPendingSendResponse(res: unknown): res is AgentCommandPendingResponse {
  return (
    typeof res === 'object' &&
    res !== null &&
    (res as { status?: unknown }).status === 'pending' &&
    typeof (res as { commandId?: unknown }).commandId === 'string'
  );
}

const UNCONFIRMED_MESSAGE =
  'Sent, but the agent stream has not confirmed delivery yet. The message may still arrive.';
const TIMEOUT_FAILED_MESSAGE = 'No response from the server. The message was not delivered.';

interface DefaultAgentStreamDataProviderProps {
  agent: Agent;
  children: ReactNode;
}

export function DefaultAgentStreamDataProvider({
  agent,
  children,
}: DefaultAgentStreamDataProviderProps) {
  const { data: events } = useAgentStream(agent.id);
  useAgentStreamSocket(agent.id, agent.streamId);
  useAgentQueueSocket(agent.id);
  const sendMessage = useSendMessage();
  const interruptAgent = useInterruptAgent();
  const stopAgent = useStopAgent();
  const completeAgent = useCompleteAgent();
  const streamActivity = useAgentActivityFromStream(agent, events ?? undefined);
  const streamTurnActivity = useMemo(
    () => deriveTurnActivity(agent, events ?? undefined),
    [agent, events],
  );
  const agentTypeRegistry = useAgentTypeRegistry();
  const supportsSteering = agentTypeRegistry.get(agent.agentType)?.supportsSteering === true;
  const supportsModelSwitch =
    agentTypeRegistry.get(agent.agentType)?.supportsRuntimeModelSwitch === true;
  const { queue, enqueue, updateMessage, removeMessage, reorder } = useMessageQueue(agent.id);

  const [pendingMessages, setPendingMessages] = useLocalStorageState<PendingUserMessage[]>(
    `reflex:pending-messages:${agent.id}`,
    [],
  );

  // Count a message this client has already sent as the turn it is about to
  // become, so the composer offers Interrupt and queues the next message behind
  // it rather than dispatching a second prompt on top.
  const activity = activityWithPendingSend(streamActivity, pendingMessages);
  const isWorking = isWorkingActivity(activity);
  const isTurnWorking = isWorkingActivity(
    activityWithPendingSend(streamTurnActivity, pendingMessages),
  );

  // Reconcile pending entries against echoed user-message events. Runs
  // both on runtime echo arrival and on rehydration after refresh /
  // navigation — same code path covers both.
  useEffect(() => {
    setPendingMessages((prev) => reconcilePending(events, prev));
  }, [events, setPendingMessages]);

  const markPendingFailed = useCallback(
    (clientId: string, errorMessage: string) => {
      let didFlip = false;
      setPendingMessages((prev) =>
        prev.map((p) => {
          if (p.clientId !== clientId) return p;
          if (p.status === 'failed') return p;
          didFlip = true;
          return {
            ...p,
            status: 'failed',
            error: errorMessage,
            attachments: stripAttachmentBlobs(p.attachments),
          };
        }),
      );
      if (didFlip) {
        toast.error('Failed to send message', { description: errorMessage });
      }
    },
    [setPendingMessages],
  );

  // A 202 means the server accepted the message but its delivery outcome
  // outlasted the server's bounded wait (a sleeping agent being woken);
  // keep the returned command id so the outcome can be watched.
  const markPendingHttpAcked = useCallback(
    (clientId: string, commandId?: string) => {
      setPendingMessages((prev) =>
        prev.map((p) =>
          p.clientId === clientId
            ? { ...p, httpAcked: true, ...(commandId ? { commandId } : {}) }
            : p,
        ),
      );
    },
    [setPendingMessages],
  );

  const sendOptimistic = useCallback(
    (clientId: string, msg: string, attachments: Attachment[]) => {
      sendMessage.mutate(
        { agentId: agent.id, message: msg, attachments },
        {
          onSuccess: (res) =>
            markPendingHttpAcked(clientId, isPendingSendResponse(res) ? res.commandId : undefined),
          onError: (err: Error) => markPendingFailed(clientId, err.message),
        },
      );
    },
    [agent.id, sendMessage, markPendingHttpAcked, markPendingFailed],
  );

  // Delivery outcome of a 202 send. A refusal is final, so it surfaces on
  // the bubble at once with the server's reason; `sent` needs no action —
  // the stream echo resolves the bubble exactly as for a 201 send.
  const onCommandOutcome = useCallback(
    (clientId: string, outcome: CommandOutcome) => {
      if (outcome.status === 'failed') {
        markPendingFailed(clientId, describeCommandFailure(outcome.failure));
      }
    },
    [markPendingFailed],
  );
  useCommandOutcomeWatch(
    pendingMessages,
    agent.id,
    api.getAgentCommandStatus,
    onCommandOutcome,
    SEND_ECHO_TIMEOUT_MS,
  );

  // Echo-timeout watchdog. When the deadline elapses we read the
  // *current* pending entry to decide between `unconfirmed` (HTTP was
  // acked → message likely reached the agent, just no echo) and
  // `failed` (HTTP never acked → message almost certainly lost).
  const onEchoTimeout = useCallback(
    (clientId: string) => {
      setPendingMessages((prev) =>
        prev.map((p) => {
          if (p.clientId !== clientId) return p;
          const next = nextStatusOnTimeout(p);
          if (!next) return p;
          return {
            ...p,
            status: next,
            error: next === 'unconfirmed' ? UNCONFIRMED_MESSAGE : TIMEOUT_FAILED_MESSAGE,
            attachments: stripAttachmentBlobs(p.attachments),
          };
        }),
      );
    },
    [setPendingMessages],
  );
  usePendingEchoTimeout(pendingMessages, onEchoTimeout, SEND_ECHO_TIMEOUT_MS);

  // Add an optimistic "sending" bubble for a message about to hit the wire and
  // return its client id. The bubble resolves when the server echoes the
  // message back via the live stream (or surfaces via the echo-timeout
  // watchdog). Shared by immediate sends and redirects.
  const addPendingBubble = useCallback(
    (msg: string, attachments: Attachment[]): string => {
      const clientId = randomClientId();
      const persistableAttachments = attachments.length
        ? attachments.map((a) => ({ name: a.name, mimeType: a.mimeType, data: a.data }))
        : undefined;
      setPendingMessages((prev) => {
        const next: PendingUserMessage[] = [
          ...prev,
          {
            clientId,
            text: msg,
            attachments: persistableAttachments,
            createdAt: Date.now(),
            status: 'sending',
          },
        ];
        return next.length > MAX_PENDING_MESSAGES
          ? next.slice(next.length - MAX_PENDING_MESSAGES)
          : next;
      });
      return clientId;
    },
    [setPendingMessages],
  );

  const send = useCallback(
    (msg: string, attachments: Attachment[]) => {
      // Drain decisions live on the server. While a turn is in flight we
      // append to the queue; otherwise we hit the regular send endpoint
      // and add an optimistic bubble that resolves when the server
      // echoes the message back via the live stream. Agent types that
      // support steering deliver mid-turn messages directly instead —
      // the provider injects them into the in-flight turn.
      // A typed `/model <id>` is the server-side switch the model chip makes.
      // It starts no turn and gets no echo, so it skips the queue and the
      // optimistic bubble. A bare `/model` falls through to the harness.
      const modelCommand = supportsModelSwitch ? parseModelCommand(msg) : null;
      if (modelCommand && modelCommand.kind !== 'report') {
        sendMessage.mutate(
          { agentId: agent.id, message: msg, attachments },
          { onError: (err: Error) => toast.error(modelSwitchErrorMessage(err)) },
        );
        return;
      }
      if (isTurnWorking && !supportsSteering) {
        enqueue(msg, attachments.length ? attachments : undefined);
        return;
      }
      // Codex handles recognized slash commands locally and emits a durable
      // command-result note instead of a user-message echo. Do not create an
      // optimistic user bubble that would spin forever waiting for that echo.
      if (agent.agentType === 'codex' && attachments.length === 0 && isCodexSlashCommand(msg)) {
        sendMessage.mutate(
          { agentId: agent.id, message: msg, attachments },
          {
            onError: (err: Error) =>
              toast.error('Failed to run command', { description: err.message }),
          },
        );
        return;
      }
      const clientId = addPendingBubble(msg, attachments);
      sendOptimistic(clientId, msg, attachments);
    },
    [
      agent.agentType,
      agent.id,
      isTurnWorking,
      supportsSteering,
      supportsModelSwitch,
      enqueue,
      sendMessage,
      sendOptimistic,
      addPendingBubble,
    ],
  );

  // Redirect: interrupt the in-flight turn, then send the message so the agent
  // picks up the new instruction immediately instead of after the current turn
  // finishes (which is what plain `send` -> `enqueue` does). We interrupt
  // first so the run is in the `interrupted` state when the message lands and
  // the server resumes it with the new content. The optimistic bubble goes up
  // right away; if the interrupt itself fails we surface that on the bubble.
  const redirect = useCallback(
    (msg: string, attachments: Attachment[]) => {
      const clientId = addPendingBubble(msg, attachments);
      interruptAgent.mutate(agent.id, {
        onSuccess: () => sendOptimistic(clientId, msg, attachments),
        onError: (err: Error) => markPendingFailed(clientId, err.message),
      });
    },
    [agent.id, interruptAgent, sendOptimistic, addPendingBubble, markPendingFailed],
  );

  // "Send now" on a queued row: pull the message out of the queue and
  // redirect it. Removing it first prevents a double-delivery — otherwise the
  // server would still drain the same row when the interrupted turn ends.
  const sendQueuedNow = useCallback(
    (id: string) => {
      const target = queue.find((m) => m.id === id);
      if (!target) return;
      removeMessage(id);
      redirect(target.text, target.attachments ?? []);
    },
    [queue, removeMessage, redirect],
  );

  const retryPending = useCallback(
    (clientId: string) => {
      const target = pendingMessages.find((p) => p.clientId === clientId);
      if (!target) return;
      setPendingMessages((prev) =>
        prev.map((p) =>
          p.clientId === clientId
            ? {
                ...p,
                status: 'sending',
                error: undefined,
                httpAcked: undefined,
                commandId: undefined,
                createdAt: Date.now(),
              }
            : p,
        ),
      );
      // Rebuild Attachment[] from the persisted preview metadata.
      // `data` is only present when the entry was just marked failed
      // in this session; after a refresh it has been stripped, so the
      // retry sends text only.
      const attachments: Attachment[] = (target.attachments ?? [])
        .filter((a): a is { name: string; mimeType: string; data: string } =>
          Boolean(a.name && a.mimeType && a.data),
        )
        .map((a, idx) => ({
          id: `${clientId}-att-${idx}`,
          type: a.mimeType.startsWith('image/') ? 'image' : 'file',
          name: a.name,
          mimeType: a.mimeType,
          data: a.data,
        }));
      sendOptimistic(clientId, target.text, attachments);
    },
    [pendingMessages, sendOptimistic, setPendingMessages],
  );

  const dismissPending = useCallback(
    (clientId: string) => {
      setPendingMessages((prev) => prev.filter((p) => p.clientId !== clientId));
    },
    [setPendingMessages],
  );

  // Synthetic optimistic bubble for a fresh agent's initial prompt —
  // shown while the agent is booting and the live stream hasn't yet
  // echoed the prompt back as a `query` / `session/prompt` event. The
  // entry is purely derived: not persisted, not part of
  // `pendingMessages`, and not subject to the echo-timeout watchdog
  // (booting can legitimately exceed the 20s threshold).
  const syntheticInitial = useMemo(() => getInitialPromptPending(agent, events), [agent, events]);

  const effectivePending = useMemo<PendingUserMessage[]>(
    () => (syntheticInitial ? [syntheticInitial, ...pendingMessages] : pendingMessages),
    [syntheticInitial, pendingMessages],
  );

  const interrupt = useCallback(() => {
    interruptAgent.mutate(agent.id);
  }, [interruptAgent, agent.id]);

  const stop = useCallback(() => {
    stopAgent.mutate(agent.id);
  }, [stopAgent, agent.id]);

  const complete = useCallback(() => {
    completeAgent.mutate(agent.id, {
      onSuccess: () => toast.success('Agent marked complete'),
      onError: (err: Error) =>
        toast.error('Failed to mark agent complete', { description: err.message }),
    });
  }, [completeAgent, agent.id]);

  const value = useMemo<AgentStreamData>(
    () => ({
      events,
      activity,
      isWorking,
      isTurnWorking,
      send,
      redirect,
      interrupt,
      stop,
      complete,
      queue,
      enqueue,
      updateMessage,
      removeMessage,
      reorderQueue: reorder,
      sendQueuedNow,
      pendingMessages: effectivePending,
      retryPending,
      dismissPending,
    }),
    [
      events,
      activity,
      isWorking,
      isTurnWorking,
      send,
      redirect,
      interrupt,
      stop,
      complete,
      queue,
      enqueue,
      updateMessage,
      removeMessage,
      reorder,
      sendQueuedNow,
      effectivePending,
      retryPending,
      dismissPending,
    ],
  );

  return (
    <AgentStreamDataContext.Provider value={value}>{children}</AgentStreamDataContext.Provider>
  );
}
